import { useEffect, useMemo, useState } from 'react';
import EntityEditDrawer from './EntityEditDrawer';
import CoreFieldForm from './CoreFieldForm';
import CustomFieldsForm from './CustomFieldsForm';
import customFieldsApi from '../../api/customFields';
import { partsApi } from '../../api/parts';
import { tariffRatesApi } from '../../api/tariffRates';

const statusOptions = [
  'Target',
  'RFQ',
  'Quoted',
  'Nominated',
  'Awarded',
  'Development',
  'Pre-Production',
  'Production',
  'End of Life',
  'Lost'
];

const toDateInput = (value) => value ? String(value).slice(0, 10) : '';

export default function PartEditDrawer({ open, onOpenChange, part, onSaved }) {
  const [formData, setFormData] = useState({});
  const [customPayload, setCustomPayload] = useState([]);
  const [tariffRates, setTariffRates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!part || !open) return;
    setFormData({
      part_number: part.part_number || '',
      description: part.description || '',
      oem: part.oem || '',
      carline: part.carline || '',
      production_country: part.production_country || '',
      production_city: part.production_city || '',
      destination_country: part.destination_country || '',
      hts_code: part.hts_code || '',
      business_status: part.business_status || '',
      contract_start_date: toDateInput(part.contract_start_date),
      contract_end_date: toDateInput(part.contract_end_date),
      sop_date: toDateInput(part.sop_date),
      eop_date: toDateInput(part.eop_date),
      ppap_status: part.ppap_status || '',
      notes: part.notes || ''
    });
    setError(null);
  }, [part, open]);

  useEffect(() => {
    const loadTariffRates = async () => {
      if (!open) return;
      try {
        const response = await tariffRatesApi.getTariffRates('', '', true);
        if (response.success) {
          setTariffRates(response.data.rates || []);
        }
      } catch (err) {
        setTariffRates([]);
      }
    };

    loadTariffRates();
  }, [open]);

  const htsOptions = useMemo(() => {
    return [...new Set(tariffRates.map((rate) => rate.hts_code).filter(Boolean))].slice(0, 50);
  }, [tariffRates]);

  const fields = [
    { name: 'part_number', label: 'Part Number', required: true },
    { name: 'description', label: 'Description' },
    { name: 'oem', label: 'OEM / Customer', required: true },
    { name: 'carline', label: 'Carline / Program Context' },
    { name: 'production_country', label: 'Production Country' },
    { name: 'production_city', label: 'Production City' },
    { name: 'destination_country', label: 'Destination Country' },
    { name: 'hts_code', label: 'HTS Code', listId: 'part-edit-hts-codes' },
    {
      name: 'business_status',
      label: 'Business Status',
      type: 'select',
      options: statusOptions
    },
    { name: 'ppap_status', label: 'PPAP Status' },
    { name: 'sop_date', label: 'SOP', type: 'date' },
    { name: 'eop_date', label: 'EOP', type: 'date' },
    { name: 'contract_start_date', label: 'Contract Start', type: 'date' },
    { name: 'contract_end_date', label: 'Contract End', type: 'date' },
    { name: 'notes', label: 'Notes', type: 'textarea', span: 2 }
  ];

  const handleSave = async () => {
    if (!part?.part_number) return;
    setSaving(true);
    setError(null);
    try {
      const response = await partsApi.updatePart(part.part_number, formData);
      if (response.success === false) {
        throw new Error(response.error || 'Failed to update part');
      }

      if (customPayload.length > 0 && part.id) {
        await customFieldsApi.setValues('part', part.id, customPayload);
      }

      await onSaved?.(response.data);
      onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update part');
    } finally {
      setSaving(false);
    }
  };

  return (
    <EntityEditDrawer
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Part"
      description="Update the core part record. Planning volume, scenario changes, and price lifecycle changes remain in their governed workflows."
      onSave={handleSave}
      saving={saving}
      error={error}
    >
      <div className="space-y-6">
        <CoreFieldForm fields={fields} values={formData} onChange={setFormData} />
        <datalist id="part-edit-hts-codes">
          {htsOptions.map((code) => <option key={code} value={code} />)}
        </datalist>
        {part?.id && (
          <CustomFieldsForm
            entityType="part"
            entityId={part.id}
            onPayloadChange={setCustomPayload}
          />
        )}
      </div>
    </EntityEditDrawer>
  );
}
